import {
  DndContext,
  useDroppable,
  useSensor,
  useSensors,
  MeasuringStrategy,
  closestCenter,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useRef } from 'react'

import { KeyboardSensor, PointerSensor } from '../../lib/dndKitSensors'
import { useFormPlaygroundStore } from '../../stores/formPlaygroundStore'
import { ScrollArea } from '../ui/scroll-area'

import FormElementCard from './FormElementCard'

export default function FormPlayground({ isDropped, resetIsDropped, isUpdate = false }) {
  const formElements = useFormPlaygroundStore((state) => state.formElements)

  const moveFormElement = useFormPlaygroundStore((state) => state.moveFormElement)

  const { setNodeRef, isOver } = useDroppable({
    id: 'droppable',
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const cardsEndRef = useRef(null)
  const scrollAreaRef = useRef(null)

  if (cardsEndRef.current && isDropped && scrollAreaRef.current) {
    cardsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    setTimeout(() => {
      resetIsDropped()
    }, 500)
  }

  function handleDragEnd({ active, over }) {
    if (!over) return

    if (active.id !== over.id) {
      const oldIndex = active.data.current?.sortable.index
      const newIndex = over.data.current?.sortable.index
      moveFormElement(oldIndex, newIndex)
    }
  }

  const playgroundHeight = isUpdate ? 'calc(100vh - 247px)' : 'calc(100vh - 212px)'

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToParentElement]}
      onDragEnd={handleDragEnd}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      collisionDetection={closestCenter}
    >
      <SortableContext items={formElements} strategy={verticalListSortingStrategy}>
        <section
          ref={setNodeRef}
          className={`flex-grow w-full rounded-lg border-2 border-dashed bg-muted/25 ${
            isOver ? 'border-muted-foreground' : 'border-slate-300'
          }`}
          style={{ height: playgroundHeight, display: 'flex', flexDirection: 'column' }}
        >
          {formElements.length === 0 ? (
            <p
              className={`flex h-full items-center justify-center text-center px-4 font-medium ${
                isOver ? 'text-slate-700' : 'text-muted-foreground'
              }`}
            >
              {isOver ? 'Drop the element here ...' : 'Drag an element from the right to this area'}
            </p>
          ) : (
            <ScrollArea ref={scrollAreaRef} className="h-full w-full" style={{ overflow: 'auto' }}>
              <div className="space-y-5 py-5 px-4 sm:px-5 md:px-5">
                {formElements.map((element) => (
                  <FormElementCard key={element.id} formElement={element} />
                ))}
                <div ref={cardsEndRef} />
              </div>
            </ScrollArea>
          )}
        </section>
      </SortableContext>
    </DndContext>
  )
}
